import { ReactNode } from 'react';

export type ObjectType = 'table' | 'hypertable' | 'cagg';

export function fmtBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  const units = ['KiB', 'MiB', 'GiB', 'TiB'];
  let v = n / 1024;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i++;
  }
  return `${v < 10 ? v.toFixed(2) : v < 100 ? v.toFixed(1) : v.toFixed(0)} ${units[i]}`;
}

export function fmtNumber(n: number): string {
  if (n < 1000) return String(n);
  if (n < 1_000_000) return `${(n / 1000).toFixed(1)}k`;
  if (n < 1_000_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  return `${(n / 1_000_000_000).toFixed(2)}B`;
}

export function fmtDate(iso: string): string {
  return new Date(iso).toISOString().slice(0, 16).replace('T', ' ');
}

export function StatCard({
  label,
  value,
  sub,
  tone,
}: {
  label: string;
  value: ReactNode;
  sub?: ReactNode;
  tone?: 'default' | 'primary' | 'success' | 'warn' | 'danger';
}) {
  const toneCls =
    tone === 'primary'
      ? 'border-primary/40 bg-primary/5'
      : tone === 'success'
      ? 'border-green-300 bg-green-50 dark:bg-green-900/20 dark:border-green-700'
      : tone === 'warn'
      ? 'border-amber-300 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-700'
      : tone === 'danger'
      ? 'border-red-300 bg-red-50 dark:bg-red-900/20 dark:border-red-700'
      : 'border-border bg-card';
  return (
    <div className={`rounded-lg border ${toneCls} p-3`}>
      <div className="text-[11px] uppercase tracking-wide text-muted-foreground font-semibold">{label}</div>
      <div className="text-lg font-bold mt-0.5">{value}</div>
      {sub && <div className="text-xs text-muted-foreground mt-0.5">{sub}</div>}
    </div>
  );
}

export function TypeBadge({ type }: { type: ObjectType }) {
  const cfg = {
    table: { label: 'table', cls: 'bg-slate-100 text-slate-700 dark:bg-slate-700/40 dark:text-slate-200 border-slate-300 dark:border-slate-600' },
    hypertable: { label: 'hypertable', cls: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-200 border-indigo-300 dark:border-indigo-700' },
    cagg: { label: 'cagg', cls: 'bg-teal-100 text-teal-700 dark:bg-teal-900/40 dark:text-teal-200 border-teal-300 dark:border-teal-700' },
  }[type];
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium border ${cfg.cls}`}>
      {cfg.label}
    </span>
  );
}

export function DataSizeBar({
  originalBytes,
  onDiskBytes,
}: {
  originalBytes: number;
  onDiskBytes: number;
}) {
  const max = Math.max(originalBytes, onDiskBytes, 1);
  const pctOriginal = (originalBytes / max) * 100;
  const pctOnDisk = (onDiskBytes / max) * 100;
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-3">
        <div className="w-24 text-xs text-muted-foreground">Original</div>
        <div className="flex-1 h-5 bg-muted rounded">
          <div className="h-5 rounded bg-amber-400" style={{ width: `${pctOriginal}%` }} />
        </div>
        <div className="w-24 text-xs font-mono text-right">{fmtBytes(originalBytes)}</div>
      </div>
      <div className="flex items-center gap-3">
        <div className="w-24 text-xs text-muted-foreground">On-disk</div>
        <div className="flex-1 h-5 bg-muted rounded">
          <div className="h-5 rounded bg-amber-600" style={{ width: `${pctOnDisk}%` }} />
        </div>
        <div className="w-24 text-xs font-mono text-right">{fmtBytes(onDiskBytes)}</div>
      </div>
      <div className="flex items-center gap-4 text-[11px] text-muted-foreground pt-1">
        <span className="flex items-center gap-1.5">
          <span className="w-2.5 h-2.5 rounded-sm bg-amber-400" /> Original (décompressé)
        </span>
        <span className="flex items-center gap-1.5">
          <span className="w-2.5 h-2.5 rounded-sm bg-amber-600" /> On-disk (compressé)
        </span>
      </div>
    </div>
  );
}

export function Section({ title, action, children }: { title: string; action?: ReactNode; children: ReactNode }) {
  return (
    <div className="bg-card border border-border rounded-lg">
      <div className="px-4 py-3 border-b border-border flex items-center justify-between">
        <h2 className="font-semibold text-sm">{title}</h2>
        {action}
      </div>
      <div className="p-4">{children}</div>
    </div>
  );
}

export function Btn({
  children,
  onClick,
  variant = 'default',
  disabled,
  size = 'md',
}: {
  children: ReactNode;
  onClick?: () => void;
  variant?: 'default' | 'primary' | 'destructive' | 'ghost';
  disabled?: boolean;
  size?: 'sm' | 'md';
}) {
  const sizeCls = size === 'sm' ? 'h-8 px-2.5 text-xs' : 'h-9 px-3 text-sm';
  const v =
    variant === 'primary'
      ? 'bg-primary text-primary-foreground hover:opacity-90'
      : variant === 'destructive'
      ? 'border border-destructive text-destructive hover:bg-destructive/10'
      : variant === 'ghost'
      ? 'hover:bg-accent text-foreground'
      : 'border border-border hover:bg-accent';
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`${sizeCls} rounded-md font-medium ${v} disabled:opacity-50 disabled:cursor-not-allowed`}
    >
      {children}
    </button>
  );
}
